export type SupportedLocale = "en" | "ko" | "zh";
export const SUPPORTED_LOCALES: SupportedLocale[] = ["en", "ko", "zh"];
export const LOCALE_STORAGE_KEY = "locale";

export const LOCALE_LABELS: Record<SupportedLocale, string> = {
	en: "English",
	ko: "한국어",
	zh: "中文",
};

export function isSupportedLocale(
	value: string | null,
): value is SupportedLocale {
	return value === "en" || value === "ko" || value === "zh";
}

export function detectLocale(): SupportedLocale {
	if (typeof navigator === "undefined") return "en";
	const lang = navigator.language.toLowerCase();
	if (lang.startsWith("ko")) return "ko";
	if (lang.startsWith("zh")) return "zh";
	return "en";
}

/**
 * Locale implied by a URL path prefix (`/ko/...`, `/ko`, `/zh/...`). Returns null
 * for the unprefixed English root. When present this is the AUTHORITATIVE locale,
 * so a prerendered `/ko/...` page renders Korean regardless of stored prefs.
 */
export function localeFromPath(pathname: string): SupportedLocale | null {
	const match = pathname.match(/^\/(ko|zh)(?:\/|$)/);
	return match ? (match[1] as SupportedLocale) : null;
}

/** Strip a leading locale prefix → the logical (English) path (`/ko/learn/x` → `/learn/x`, `/ko` → `/`). */
export function stripLocalePrefix(pathname: string): string {
	const locale = localeFromPath(pathname);
	if (!locale) return pathname;
	const rest = pathname.slice(locale.length + 1);
	return rest === "" ? "/" : rest;
}

/** Re-apply a locale prefix to a logical path (`/` → `/ko`, `/learn/x` → `/ko/learn/x`). */
export function withLocalePrefix(
	path: string,
	locale: SupportedLocale,
): string {
	if (locale === "en") return path;
	return path === "/" ? `/${locale}` : `/${locale}${path}`;
}

/**
 * Logical paths that have locale-prefixed mirrors (Phase 1: landing + learn).
 * Deliberately loose on the slug so router patterns like `/learn/$slug`
 * localize too; strict slug validation stays in seo.ts (`isLocalizablePath`)
 * where inputs are arbitrary URLs.
 */
export function isLocalizableLinkPath(path: string): boolean {
	return path === "/" || path === "/learn" || path.startsWith("/learn/");
}

/**
 * URL for `logicalPath` in `locale`, or null when that locale has no URL for
 * it (non-localizable page, or a locale without prefixed routes — zh).
 */
export function localizedPathFor(
	logicalPath: string,
	locale: SupportedLocale,
): string | null {
	if (!isLocalizableLinkPath(logicalPath)) return null;
	if (locale === "en") return logicalPath;
	// zh is UI-only for now (no /zh routes) — never emit a /zh/... href.
	if (locale !== "ko") return null;
	return withLocalePrefix(logicalPath, locale);
}

export function readLocale(): SupportedLocale {
	if (typeof window === "undefined") return "en";
	// URL prefix wins (authoritative for SSR/prerender), then stored, then browser.
	const fromPath = localeFromPath(window.location.pathname);
	if (fromPath) return fromPath;
	const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
	if (isSupportedLocale(stored)) return stored;
	return detectLocale();
}

export function writeLocale(locale: SupportedLocale): void {
	if (typeof window === "undefined") return;
	window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
}

export function applyLocale(locale: SupportedLocale): void {
	if (typeof document === "undefined") return;
	document.documentElement.lang = locale;
}

export const LOCALE_INIT_SCRIPT = `(function(){try{var supported=['en','ko','zh'];var m=(window.location.pathname||'/').match(/^\\/(ko|zh)(?:\\/|$)/);var locale=m?m[1]:(function(){var stored=window.localStorage.getItem('locale');if(supported.indexOf(stored)!==-1)return stored;var lang=navigator.language.toLowerCase();if(lang.indexOf('ko')===0)return'ko';if(lang.indexOf('zh')===0)return'zh';return'en';})();document.documentElement.lang=locale;}catch(e){}})();`;
